<template>
  <MultiSizeTotalSummary v-if="!isMultiUpload && enableAbAiMonetization" :index="index" />
  <div
    v-else-if="pricing"
    class="multi-size-total"
    :class="{ 'multi-size-total--multi-upload': isMultiUpload }"
  >
    <div class="multi-size-total__container">
      <div class="multi-size-total__label multi-size-total__label--total">Total</div>
      <div v-if="sizesCount > 0" class="multi-size-total__label multi-size-total__label--sizes">
        for {{ t("transfer", quantity) }}
      </div>
    </div>
    <div
      v-if="sizesCount > 0 && total && showPricing"
      class="multi-size-total__container multi-size-total__container--horizontal"
      :class="{
        'multi-size-total__container--loading': isPricingLoading || total === 0
      }"
    >
      <div class="multi-size-total__label">{{ n(total, "currency") }}</div>
      <div
        v-if="retailTotal && discount > 0"
        class="multi-size-total__label multi-size-total__label--strike-of"
      >
        {{ n(retailTotal, "currency") }}
      </div>
      <div v-if="discount > 0" class="multi-size-total__label multi-size-total__label--discounted">
        {{ discount }}% off
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from "pinia";
import { useI18n } from "vue-i18n";
import { n } from "../../vue-cart/helpers/currency";
import { useDtfStore } from "../store";
// During ab_ai_monetization cleanup, change this component to handle only MultiUpload case
import MultiSizeTotalSummary from "./MultiSizeTotalSummary.vue";

export default {
  name: "MultiSizeTotal",
  components: {
    MultiSizeTotalSummary
  },
  props: {
    index: {
      type: Number,
      default: 0
    },
    isMultiUpload: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { t } = useI18n();
    return { n, t };
  },
  computed: {
    ...mapState(useDtfStore, [
      "artworks",
      "summary",
      "simulation",
      "isPricingLoading",
      "enableAbAiMonetization"
    ]),
    artwork() {
      return this.artworks[this.index];
    },
    isArtworkUploaded() {
      const artworkId = parseInt(this.artwork?.id, 10);
      if (Number.isNaN(artworkId)) {
        return false;
      }

      return typeof artworkId === "number" && !this.artwork?.isLoading;
    },
    showPricing() {
      return this.index === 0 || this.isArtworkUploaded;
    },
    sizesAndQuantities() {
      return this.artwork?.sizesAndQuantities || [];
    },
    sizesCount() {
      return this.sizesAndQuantities.length;
    },
    quantity() {
      return this.sizesAndQuantities.reduce((acc, curr) => acc + Number(curr.quantity), 0);
    },
    pricing() {
      if (this.artwork?.isFile || !this.sizesAndQuantities.some(sq => Number(sq.quantity) > 0)) {
        return this.simulation;
      }

      return this.sizesAndQuantities || this.simulation;
    },
    retailTotal() {
      return this.getPrice("retailPrice");
    },
    total() {
      return this.getPrice("unitPrice");
    },
    discount() {
      const retail = parseFloat(this.retailTotal);
      const actual = parseFloat(this.total);
      if (!retail || !actual) return 0;
      return Math.ceil(((retail - actual) / retail) * 100).toFixed(0);
    }
  },
  methods: {
    getPrice(propertyName) {
      if (Array.isArray(this.pricing)) {
        return this.pricing
          .reduce((sum, item) => {
            const price = Number(item[propertyName] || 0);
            return sum + price * item.quantity;
          }, 0)
          .toFixed(2);
      }

      return this.pricing[propertyName]
        ? Number(this.pricing[propertyName]).toFixed(2) * this.pricing.quantity
        : null;
    }
  }
};
</script>
<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
.multi-size-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 60px;

  &--multi-upload {
    background-color: $crafterpay-gray-modern-100;
    justify-content: space-between !important;
    padding: 10px 15px;
    width: 100%;
    margin-top: 20px;
    border-radius: 4px;
  }

  @include with-desktop {
    justify-content: flex-start;
  }

  &__container {
    display: flex;
    flex-direction: column;

    &--horizontal {
      gap: 5px;
      flex-direction: row;
    }

    &--loading {
      opacity: 0.25;
    }
  }

  &__label {
    font-family: $font-family-secondary;
    font-size: 20px;
    font-weight: 700;
    line-height: 20px;
    text-align: left;

    &--sizes {
      font-weight: 400;
      font-size: 14px;
      line-height: 14px;
    }

    &--total {
      font-size: 17px;
    }

    &--strike-of {
      color: $warm-grey;
      font-size: 12px;
      font-weight: 400;
      text-decoration: line-through;
    }

    &--discounted {
      font-size: 14px;
      color: $feedback-success-darker;
    }
  }
}
</style>
